<script setup>
// §8.5 TextField — label + input via LField. Border is a graphical object,
// 3:1 min (§7); focus is the 2px ring (base.css). Numeric inputs get tabular.
import LField from './Field.vue'

defineProps({
	label: { type: String, default: null },
	hint: { type: String, default: null },
	error: { type: String, default: null },
	type: { type: String, default: 'text' },
	placeholder: { type: String, default: '' },
	// Passed straight through to the input so password managers and browser
	// autofill can do their job (sign-in and address forms especially).
	autocomplete: { type: String, default: null },
	numeric: { type: Boolean, default: false },
	required: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	// Native range bounds for `type="number"` (and the date/time types). Not
	// validation — the browser only enforces these on a form submit.
	min: { type: [Number, String], default: null },
	max: { type: [Number, String], default: null },
	step: { type: [Number, String], default: null },
	// Hard limit on what can be typed (a 4-digit PIN, a fixed-length code).
	// Not validation — pair it with `error` when the length actually matters.
	maxlength: { type: [Number, String], default: null },
	// Values to suggest as the user types (a native datalist); anything else can still be typed.
	suggestions: { type: Array, default: () => [] },
})
const model = defineModel({ default: '' })
</script>

<template>
	<LField :label="label" :hint="hint" :error="error" :required="required" v-slot="{ id, invalid }">
		<input :id="id" v-model="model" :type="type" :placeholder="placeholder" :maxlength="maxlength"
			:min="min" :max="max" :step="step" :disabled="disabled"
			:autocomplete="autocomplete" :aria-invalid="invalid" class="input" :class="{ invalid, num: numeric }"
			:list="suggestions.length ? id + '-suggestions' : null" />
		<datalist v-if="suggestions.length" :id="id + '-suggestions'">
			<option v-for="suggestion in suggestions" :key="suggestion" :value="suggestion" />
		</datalist>
	</LField>
</template>

<style scoped>
.input
{
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--text);
	padding: 9px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	min-height: var(--target-min);
	width: 100%;
}

.input::placeholder
{
	color: var(--text-3);
}

.input:hover:not(:disabled)
{
	border-color: var(--line-strong);
}

.input:disabled
{
	background: var(--surface-sunken);
	color: var(--text-3);
	cursor: not-allowed;
}

.input:focus-visible
{
	border-color: var(--interactive);
}

.input.invalid
{
	border-color: var(--status-rejected-text);
}

.input.num
{
	font-variant-numeric: tabular-nums;
	font-feature-settings: 'tnum' 1;
}
</style>
